import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  useMeNotificationPreferenceQuery,
  UserNotificationPreference,
  UserNotificationPreferenceKind,
  UserNotificationPreferenceUpdateArgs,
  useUserNotificationPreferenceUpdateMutation,
} from '../../services';

interface NotificationPreferenceChange {
  kind: UserNotificationPreferenceKind;
  isEnabled: boolean;
}

export const useNotificationPreference = () => {
  const { t } = useTranslation();

  const { data: fetchedPreference } = useMeNotificationPreferenceQuery();

  const [updatePreference] = useUserNotificationPreferenceUpdateMutation();

  const [localPreference, setLocalPreference] = useState<UserNotificationPreference>();

  const preference = localPreference ?? fetchedPreference;

  const savePreference = useCallback(
    async ({ kind, fields }: UserNotificationPreferenceUpdateArgs) => {
      if (!preference) {
        return;
      }

      setLocalPreference({ ...preference, [kind]: { ...preference[kind], ...fields } });

      const revertPreference = () => {
        setLocalPreference(preference);
        toast.error(t('common.error-message'));
      };

      try {
        const updatedPreference = await updatePreference({ kind, fields }).unwrap();

        if (!updatedPreference) {
          revertPreference();
          return;
        }

        setLocalPreference(updatedPreference);
      } catch (_) {
        revertPreference();
      }
    },
    [preference, t, updatePreference]
  );

  const setPushEnabled = useCallback(
    ({ kind, isEnabled }: NotificationPreferenceChange) =>
      savePreference({ kind, fields: { push: isEnabled } }),
    [savePreference]
  );

  const setEmailEnabled = useCallback(
    ({ kind, isEnabled }: NotificationPreferenceChange) =>
      savePreference({ kind, fields: { email: isEnabled } }),
    [savePreference]
  );

  return useMemo(
    () => ({ preference, setPushEnabled, setEmailEnabled }),
    [preference, setEmailEnabled, setPushEnabled]
  );
};
